import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { RawHomepageSection } from "../types";

// Interface definitions
export interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: "Administrator" | "Seller" | "Buyer";
  createdAt: string;
  updatedAt: string;
}

export interface Product {
  _id: string;
  name: string;
  description: string;
  price: number;
  categoryIds: string[];
  stock: number;
  status: "active" | "inactive" | "archived";
  imageIdentifier: string;
  galleryImageIdentifiers: string[];
  attributes: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  slug: string;
}

export interface Category {
  _id: string;
  name: string;
  description?: string;
  slug: string;
  parentId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BlogPost {
  _id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  publicationDate: string;
  author: string;
  categories: string[];
  tags: string[];
  featuredImageIdentifier: string;
  status: "published" | "draft";
  createdAt: string;
  updatedAt: string;
}

// Define the base URL for the API. Should come from env variables in a real app.
const API_BASE_URL = "https://codista.onrender.com/api"; // Hardcoded to ensure it works

// Debug: Log the API base URL being used
console.log("🔧 API_BASE_URL:", API_BASE_URL);
console.log(
  "🔧 process.env.REACT_APP_API_BASE_URL:",
  process.env.REACT_APP_API_BASE_URL
);

// Get the marketplace ID from environment variables
const MARKETPLACE_ID = process.env.REACT_APP_DEFAULT_MARKETPLACE || "sw";

// Define a type for user info stored in localStorage
interface StoredUserInfo {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: "Administrator" | "Seller" | "Buyer";
  token: string;
}

const apiService = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    // Add a custom header to override the marketplace detection in remote backend
    "X-Marketplace-ID": MARKETPLACE_ID,
  },
  withCredentials: true,
});

// Add a request interceptor to include the token in headers
apiService.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Debug: Log the actual request being made
    console.log("🔍 Axios Request Interceptor:");
    console.log("  - URL:", config.url);
    console.log("  - Base URL:", config.baseURL);
    console.log("  - Full URL:", `${config.baseURL}${config.url}`);
    console.log("  - Method:", config.method);

    // Add type for config
    const userInfoString = localStorage.getItem("userInfo");
    if (userInfoString) {
      try {
        // Safely parse user info
        const userInfo: StoredUserInfo = JSON.parse(userInfoString);
        if (userInfo && userInfo.token) {
          // Ensure headers object exists
          config.headers = config.headers || {};
          config.headers["Authorization"] = `Bearer ${userInfo.token}`;
        }
      } catch (e) {
        console.error("Error parsing user info from localStorage", e);
        // Optionally clear corrupted item
        // localStorage.removeItem('userInfo');
      }
    }
    return config;
  },
  (error: AxiosError) => {
    // Add type for error
    return Promise.reject(error);
  }
);

// Optional: Add a response interceptor for global error handling
apiService.interceptors.response.use(
  (response: AxiosResponse) => response, // Add type for response
  (error: AxiosError) => {
    // Add type for error
    if (error.response && error.response.status === 401) {
      console.log("API Interceptor: Unauthorized (401). Clearing user info.");
      localStorage.removeItem("userInfo");
      // Consider using a more robust way to handle navigation/state update
      // e.g., emitting an event or using a shared state management solution.
      // window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Cart service functions
interface CartItem {
  productId: {
    _id: string;
    name: string;
    description: string;
    price: number;
    images?: string[];
    slug: string;
  };
  quantity: number;
  price: number;
}

interface Cart {
  _id: string;
  userId: string;
  items: CartItem[];
  createdAt: string;
  updatedAt: string;
  totalPrice?: number;
}

// Get cart contents
export const getCart = async (): Promise<Cart> => {
  const response = await apiService.get("/cart");
  return response.data;
};

// Add item to cart
export const addToCart = async (
  productId: string,
  quantity = 1
): Promise<Cart> => {
  const response = await apiService.post("/cart/add", { productId, quantity });
  return response.data;
};

// Update item quantity
export const updateCartItem = async (
  productId: string,
  quantity: number
): Promise<Cart> => {
  const response = await apiService.put("/cart/update", {
    productId,
    quantity,
  });
  return response.data;
};

// Remove item from cart
export const removeFromCart = async (productId: string): Promise<Cart> => {
  const response = await apiService.delete("/cart/remove", {
    data: { productId },
  });
  return response.data;
};

// Order interfaces
interface Address {
  name: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  phone: string;
}

interface OrderItem {
  productId: string;
  name: string;
  quantity: number;
  price: number;
  image?: string;
}

export interface Order {
  _id: string;
  userId: string;
  orderNumber: string;
  items: OrderItem[];
  shippingAddress: Address;
  billingAddress?: Address;
  paymentMethod: string;
  paymentStatus: string;
  paymentDetails?: Record<string, any>;
  subtotal: number;
  tax: number;
  shippingCost: number;
  total: number;
  status: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateOrderInput {
  shippingAddress: Address;
  billingAddress?: Address;
  paymentMethod: string;
  taxRate?: number;
  shippingCost?: number;
}

// Create a new order
export const createOrder = async (
  orderData: CreateOrderInput
): Promise<Order> => {
  const response = await apiService.post("/orders", orderData);
  return response.data;
};

// Get order history
export const getOrderHistory = async (): Promise<Order[]> => {
  const response = await apiService.get("/orders");
  return response.data;
};

// Get order details by ID
export const getOrderById = async (orderId: string): Promise<Order> => {
  const response = await apiService.get(`/orders/${orderId}`);
  return response.data;
};

// Product/Category Types (Assuming basic structures, adjust as needed)
// export interface Category {
//   _id: string;
//   name: string;
//   slug: string;
//   description?: string;
//   parent?: string; // Optional parent ID for hierarchy
//   children?: Category[]; // Optional children for hierarchy
// }

export interface ProductsApiResponse {
  products: Product[];
  totalPages: number;
  currentPage: number;
  totalProducts: number;
  searchQuery?: string;
}

// Product/Category API Functions

export const getProductList = async (
  params?: URLSearchParams
): Promise<ProductsApiResponse> => {
  const response = await apiService.get<ProductsApiResponse>("/products", {
    params,
  });
  return response.data;
};

export const getCategories = async (): Promise<Category[]> => {
  const response = await apiService.get<Category[]>("/categories");
  return response.data;
};

export const getProductBySlug = async (slug: string): Promise<Product> => {
  const response = await apiService.get<Product>(`/products/${slug}`);
  return response.data;
};

/**
 * Search products by keyword
 * @param keyword The search term
 * @param page Optional page number (default: 1)
 * @param limit Optional items per page (default: defined by API)
 * @returns Search results with pagination info
 */
export const searchProducts = async (
  keyword: string,
  page: number = 1,
  limit?: number
): Promise<ProductsApiResponse> => {
  const params = new URLSearchParams();
  params.append("q", keyword);
  params.append("page", page.toString());
  if (limit) params.append("limit", limit.toString());

  const response = await apiService.get<ProductsApiResponse>(
    "/products/search",
    { params }
  );
  return response.data;
};

// Content related interfaces and API calls
export interface ContentPage {
  _id: string;
  slug: string;
  title: string;
  content: string;
  metaTitle?: string;
  metaDescription?: string;
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
}

export const getContentPageBySlug = async (
  slug: string
): Promise<ContentPage> => {
  const response = await apiService.get(`/content/pages/${slug}`);
  return response.data;
};

// Get homepage sections - UPDATED RETURN TYPE
export const getHomepageSections = async (): Promise<RawHomepageSection[]> => {
  const response = await apiService.get("/content/homepage");
  return response.data;
};

// Get standard pages for navigation
export const getStandardPages = async (): Promise<
  { slug: string; title: string }[]
> => {
  const response = await apiService.get("/content/standard-pages");
  return response.data;
};

// User Management Types

interface AuthResponse {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: "Administrator" | "Seller" | "Buyer";
  token: string;
}

export interface LoginInput {
  email: string;
  password: string;
}

export interface RegisterInput {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

export interface UpdateProfileInput {
  firstName?: string;
  lastName?: string;
}

// User Management API Functions

export const login = async (credentials: LoginInput): Promise<AuthResponse> => {
  const response = await apiService.post<AuthResponse>(
    "/users/login",
    credentials
  );
  return response.data;
};

export const register = async (
  userData: RegisterInput
): Promise<AuthResponse> => {
  const response = await apiService.post<AuthResponse>(
    "/users/register",
    userData
  );
  return response.data;
};

export const getUserProfile = async (): Promise<User> => {
  const response = await apiService.get<User>("/users/profile");
  return response.data;
};

export const updateUserProfile = async (
  profileData: UpdateProfileInput
): Promise<User> => {
  const response = await apiService.put<User>("/users/profile", profileData);
  return response.data;
};

// Blog related interfaces and API calls
export interface BlogsApiResponse {
  blogs: BlogPost[];
  page: number;
  totalPages: number;
  totalBlogs: number;
  searchQuery?: string;
}

export interface BlogCategory {
  category: string;
  count: number;
}

export interface BlogTag {
  tag: string;
  count: number;
}

// Get blog posts (with optional filtering & pagination)
export const getBlogs = async (
  params?: URLSearchParams
): Promise<BlogsApiResponse> => {
  const response = await apiService.get<BlogsApiResponse>("/blog", { params });
  return response.data;
};

// Get a single blog post by slug
export const getBlogBySlug = async (slug: string): Promise<BlogPost> => {
  const response = await apiService.get<BlogPost>(`/blog/${slug}`);
  return response.data;
};

// Search blog posts
export const searchBlogs = async (
  keyword: string,
  page: number = 1,
  limit?: number
): Promise<BlogsApiResponse> => {
  const params = new URLSearchParams();
  params.append("q", keyword);
  params.append("page", page.toString());
  if (limit) params.append("limit", limit.toString());

  const response = await apiService.get<BlogsApiResponse>("/blog/search", {
    params,
  });
  return response.data;
};

// Get blog categories
export const getBlogCategories = async (): Promise<BlogCategory[]> => {
  const response = await apiService.get<BlogCategory[]>("/blog/categories");
  return response.data;
};

// Get blog tags
export const getBlogTags = async (): Promise<BlogTag[]> => {
  const response = await apiService.get<BlogTag[]>("/blog/tags");
  return response.data;
};

// Project related interfaces and API calls
export interface Project {
  _id: string;
  title: string;
  slug: string;
  shortDescription: string;
  fullDescription: string;
  featuredImageIdentifier: string;
  galleryImageIdentifiers: string[];
  startDate: string;
  endDate?: string;
  technologiesUsed: string[];
  status: "completed" | "ongoing" | "planned";
  isFeatured: boolean;
  clientName?: string;
  clientTestimonial?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectsApiResponse {
  projects: Project[];
  page: number;
  totalPages: number;
  totalProjects: number;
  searchQuery?: string;
}

export interface ProjectTechnology {
  technology: string;
  count: number;
}

// Get projects (with optional filtering & pagination)
export const getProjects = async (
  params?: URLSearchParams
): Promise<ProjectsApiResponse> => {
  const response = await apiService.get<ProjectsApiResponse>("/projects", {
    params,
  });
  return response.data;
};

// Get a single project by slug
export const getProjectBySlug = async (slug: string): Promise<Project> => {
  const response = await apiService.get<Project>(`/projects/${slug}`);
  return response.data;
};

// Get featured projects (for homepage)
export const getFeaturedProjects = async (
  limit: number = 3
): Promise<ProjectsApiResponse> => {
  const params = new URLSearchParams();
  params.append("featured", "true");
  params.append("limit", limit.toString());

  const response = await apiService.get<ProjectsApiResponse>("/projects", {
    params,
  });
  return response.data;
};

// Get projects by status
export const getProjectsByStatus = async (
  status: "completed" | "ongoing" | "planned",
  page: number = 1,
  limit: number = 10
): Promise<ProjectsApiResponse> => {
  const params = new URLSearchParams();
  params.append("status", status);
  params.append("page", page.toString());
  params.append("limit", limit.toString());

  const response = await apiService.get<ProjectsApiResponse>("/projects", {
    params,
  });
  return response.data;
};

// Get projects by technology
export const getProjectsByTechnology = async (
  technology: string,
  page: number = 1,
  limit: number = 10
): Promise<ProjectsApiResponse> => {
  const params = new URLSearchParams();
  params.append("technology", technology);
  params.append("page", page.toString());
  params.append("limit", limit.toString());

  const response = await apiService.get<ProjectsApiResponse>("/projects", {
    params,
  });
  return response.data;
};

// Search projects
export const searchProjects = async (
  keyword: string,
  page: number = 1,
  limit?: number
): Promise<ProjectsApiResponse> => {
  const params = new URLSearchParams();
  params.append("q", keyword);
  params.append("page", page.toString());
  if (limit) params.append("limit", limit.toString());

  const response = await apiService.get<ProjectsApiResponse>(
    "/projects/search",
    { params }
  );
  return response.data;
};

// Get all technologies used in projects
export const getProjectTechnologies = async (): Promise<
  ProjectTechnology[]
> => {
  const response = await apiService.get("/projects/technologies");
  return response.data;
};

// Service Request interfaces and functions
export interface ServiceRequest {
  _id: string;
  userId?: string;
  subject: string;
  description: string;
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  submissionDate: string;
  status: "pending" | "in_progress" | "completed" | "rejected";
  adminNotes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ServiceRequestsApiResponse {
  serviceRequests: ServiceRequest[];
  page: number;
  totalPages: number;
  total: number;
}

export interface ServiceRequestInput {
  subject: string;
  description: string;
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
}

// Submit a service request
export const submitServiceRequest = async (
  requestData: ServiceRequestInput
): Promise<{ success: boolean; message: string; requestId: string }> => {
  console.log(
    "🚀 submitServiceRequest called with baseURL:",
    apiService.defaults.baseURL
  );
  console.log(
    "🚀 Full URL will be:",
    `${apiService.defaults.baseURL}/requests/submit`
  );
  console.log("🚀 Request data:", requestData);

  const response = await apiService.post("/requests/submit", requestData);
  return response.data;
};

// Get all service requests (admin only)
export const getServiceRequests = async (
  page: number = 1,
  limit: number = 10,
  status?: string
): Promise<ServiceRequestsApiResponse> => {
  const params = new URLSearchParams();
  params.append("page", page.toString());
  params.append("limit", limit.toString());
  if (status) {
    params.append("status", status);
  }

  const response = await apiService.get(`/requests?${params.toString()}`);
  return response.data;
};

// Get service request by ID (admin only)
export const getServiceRequestById = async (
  id: string
): Promise<ServiceRequest> => {
  const response = await apiService.get(`/requests/${id}`);
  return response.data;
};

// Update service request status (admin only)
export const updateServiceRequestStatus = async (
  id: string,
  status: "pending" | "in_progress" | "completed" | "rejected",
  adminNotes?: string
): Promise<{
  success: boolean;
  message: string;
  serviceRequest: ServiceRequest;
}> => {
  const response = await apiService.put(`/requests/${id}`, {
    status,
    adminNotes,
  });
  return response.data;
};

// Chatbot interfaces and functions
export interface ChatMessage {
  _id: string;
  sessionId: string;
  userId?: string;
  isUserMessage: boolean;
  content: string;
  timestamp: string;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface ChatbotResponse {
  botResponse: string;
  sessionId: string;
}

export interface ChatHistoryResponse {
  sessionId: string;
  messages: ChatMessage[];
}

// Send a message to the chatbot and get a response
export const sendChatMessage = async (
  message: string,
  sessionId?: string
): Promise<ChatbotResponse> => {
  const response = await apiService.post("/chatbot/converse", {
    message,
    sessionId,
  });
  return response.data;
};

// Get conversation history for a specific session
export const getChatHistory = async (
  sessionId: string
): Promise<ChatHistoryResponse> => {
  const response = await apiService.get(`/chatbot/history/${sessionId}`);
  return response.data;
};

// ========================
// Admin API Functions
// ========================

// Get all users (Admin)
export const adminGetUsers = async (): Promise<User[]> => {
  // The AdminRoute protection handles authorization, so we just call the endpoint
  const response = await apiService.get<User[]>("/admin/users");
  return response.data;
};

// Get user by ID (Admin)
export const adminGetUserById = async (id: string): Promise<User> => {
  const response = await apiService.get<User>(`/admin/users/${id}`);
  return response.data;
};

// Update user (Admin)
export const adminUpdateUser = async (
  id: string,
  userData: Partial<Omit<User, "_id" | "createdAt" | "updatedAt">>
): Promise<User> => {
  const response = await apiService.put<User>(`/admin/users/${id}`, userData);
  return response.data;
};

// Delete user (Admin)
export const adminDeleteUser = async (id: string): Promise<void> => {
  await apiService.delete(`/admin/users/${id}`);
};

// Define the expected structure for dashboard stats
export interface DashboardStats {
  users: {
    total: number;
  };
  products: {
    total: number;
    active: number;
    lowStock: number;
  };
  orders: {
    total: number;
    pending: number;
    processing: number;
    shipped: number;
    delivered: number;
  };
  revenue: {
    total: number;
  };
  recentOrders: Array<{
    _id: string;
    orderNumber: string;
    total: number;
    status: string;
    createdAt: string;
  }>;
}

// Get dashboard stats (Admin)
export const adminGetDashboardStats = async (): Promise<DashboardStats> => {
  const response = await apiService.get<DashboardStats>("/admin/stats");
  return response.data;
};

// --- Admin Product Management ---

// Interface for creating/updating products (match backend expectations)
export interface ProductInput {
  name: string;
  description: string;
  price: number;
  categoryIds: string[];
  stock: number;
  status?: "active" | "inactive" | "archived";
  imageIdentifier?: string;
  galleryImageIdentifiers?: string[];
  attributes?: Record<string, unknown>;
  slug?: string; // Make slug optional as it might be auto-generated
}

// Get all products for the current marketplace (Admin)
export const adminGetProducts = async (): Promise<Product[]> => {
  // Backend uses marketplace context middleware based on Host header
  const response = await apiService.get<Product[]>("/admin/products");
  return response.data;
};

// Create a new product (Admin)
export const adminCreateProduct = async (
  product: ProductInput
): Promise<Product> => {
  const response = await apiService.post<Product>("/admin/products", product);
  return response.data;
};

// Get a single product by ID (Admin)
export const adminGetProductById = async (id: string): Promise<Product> => {
  const response = await apiService.get<Product>(`/admin/products/${id}`);
  return response.data;
};

// Update product (Admin)
export const adminUpdateProduct = async (
  id: string,
  product: Partial<ProductInput>
): Promise<Product> => {
  const response = await apiService.put<Product>(
    `/admin/products/${id}`,
    product
  );
  return response.data;
};

// Delete product (Admin)
export const adminDeleteProduct = async (id: string): Promise<void> => {
  await apiService.delete(`/admin/products/${id}`);
};

// Category management API calls
export const adminGetCategories = async (): Promise<Category[]> => {
  const response = await apiService.get<Category[]>("/admin/categories");
  return response.data;
};

// Blog management API calls
export const adminGetBlogPosts = async (): Promise<BlogPost[]> => {
  const response = await apiService.get<BlogPost[]>("/admin/blog/posts");
  return response.data;
};

export const adminCreateBlogPost = async (
  blogPost: Omit<BlogPost, "_id" | "createdAt" | "updatedAt">
): Promise<BlogPost> => {
  const response = await apiService.post<BlogPost>(
    "/admin/blog/posts",
    blogPost
  );
  return response.data;
};

export const adminGetBlogPostById = async (id: string): Promise<BlogPost> => {
  const response = await apiService.get<BlogPost>(`/admin/blog/posts/${id}`);
  return response.data;
};

export const adminUpdateBlogPost = async (
  id: string,
  blogPost: Partial<Omit<BlogPost, "_id" | "createdAt" | "updatedAt">>
): Promise<BlogPost> => {
  const response = await apiService.put<BlogPost>(
    `/admin/blog/posts/${id}`,
    blogPost
  );
  return response.data;
};

export const adminDeleteBlogPost = async (id: string): Promise<void> => {
  await apiService.delete(`/admin/blog/posts/${id}`);
};

// Category Management Interfaces and Functions
export interface CategoryInput {
  name: string;
  description?: string;
  slug?: string; // Make slug optional as it might be auto-generated
  parentId?: string | null;
}

export const adminCreateCategory = async (
  categoryData: CategoryInput
): Promise<Category> => {
  const response = await apiService.post<Category>(
    "/admin/categories",
    categoryData
  );
  return response.data;
};

export const adminGetCategoryById = async (id: string): Promise<Category> => {
  const response = await apiService.get<Category>(`/admin/categories/${id}`);
  return response.data;
};

export const adminUpdateCategory = async (
  id: string,
  categoryData: Partial<CategoryInput>
): Promise<Category> => {
  const response = await apiService.put<Category>(
    `/admin/categories/${id}`,
    categoryData
  );
  return response.data;
};

export const adminDeleteCategory = async (id: string): Promise<void> => {
  await apiService.delete(`/admin/categories/${id}`);
};

// Content Management Interfaces and Functions
export interface ContentPageInput {
  title: string;
  slug: string;
  content: string;
  metaTitle?: string;
  metaDescription?: string;
  isPublished: boolean;
}

export const adminGetContentPages = async (): Promise<ContentPage[]> => {
  const response = await apiService.get<ContentPage[]>("/admin/content/pages");
  return response.data;
};

export const adminGetContentPageById = async (
  id: string
): Promise<ContentPage> => {
  const response = await apiService.get<ContentPage>(
    `/admin/content/pages/${id}`
  );
  return response.data;
};

export const adminCreateContentPage = async (
  pageData: ContentPageInput
): Promise<ContentPage> => {
  const response = await apiService.post<ContentPage>(
    "/admin/content/pages",
    pageData
  );
  return response.data;
};

export const adminUpdateContentPage = async (
  id: string,
  pageData: Partial<ContentPageInput>
): Promise<ContentPage> => {
  const response = await apiService.put<ContentPage>(
    `/admin/content/pages/${id}`,
    pageData
  );
  return response.data;
};

export const adminDeleteContentPage = async (id: string): Promise<void> => {
  await apiService.delete(`/admin/content/pages/${id}`);
};

// Homepage Section Management
export interface HomepageSectionInput {
  sectionIdentifier: string; // Required by backend
  sectionType: string; // Required by backend (should be: 'hero', 'about', 'featured_products', etc)
  title?: string;
  content?: string;
  imageIdentifier?: string;
  buttonText?: string;
  buttonLink?: string;
  order?: number;
  isActive?: boolean;
  itemIds?: string[];
}

export const adminGetHomepageSections = async (): Promise<
  RawHomepageSection[]
> => {
  const response = await apiService.get<RawHomepageSection[]>(
    "/admin/content/homepage-sections"
  );
  return response.data;
};

export const adminGetHomepageSectionById = async (
  sectionId: string
): Promise<RawHomepageSection> => {
  const response = await apiService.get<RawHomepageSection>(
    `/admin/content/homepage-sections/${sectionId}`
  );
  return response.data;
};

export const adminCreateHomepageSection = async (
  sectionData: HomepageSectionInput
): Promise<RawHomepageSection> => {
  const response = await apiService.post<RawHomepageSection>(
    "/admin/content/homepage-sections",
    sectionData
  );
  return response.data;
};

export const adminUpdateHomepageSection = async (
  id: string,
  sectionData: Partial<HomepageSectionInput>
): Promise<RawHomepageSection> => {
  const response = await apiService.put<RawHomepageSection>(
    `/admin/content/homepage-sections/${id}`,
    sectionData
  );
  return response.data;
};

export const adminDeleteHomepageSection = async (
  sectionId: string
): Promise<void> => {
  await apiService.delete(`/admin/content/homepage-sections/${sectionId}`);
};

// Order Management
export interface OrdersApiResponse {
  orders: Order[];
  page: number;
  totalPages: number;
  totalOrders: number;
}

export const adminGetOrders = async (
  params?: URLSearchParams
): Promise<OrdersApiResponse> => {
  const response = await apiService.get<OrdersApiResponse>("/admin/orders", {
    params,
  });
  return response.data;
};

export const adminGetOrderById = async (id: string): Promise<Order> => {
  const response = await apiService.get<Order>(`/admin/orders/${id}`);
  return response.data;
};

export const adminUpdateOrderStatus = async (
  id: string,
  status: string,
  notes?: string
): Promise<Order> => {
  const response = await apiService.put<Order>(`/admin/orders/${id}/status`, {
    status,
    notes,
  });
  return response.data;
};

// Project Management
export interface ProjectInput {
  title: string;
  slug: string;
  shortDescription: string;
  fullDescription: string;
  featuredImageIdentifier: string;
  galleryImageIdentifiers: string[];
  startDate: string;
  endDate?: string;
  technologiesUsed: string[];
  status: "completed" | "ongoing" | "planned";
  isFeatured: boolean;
  clientName?: string;
  clientTestimonial?: string;
}

export const adminGetProjects = async (): Promise<Project[]> => {
  const response = await apiService.get<Project[]>("/admin/projects");
  return response.data;
};

export const adminGetProjectById = async (id: string): Promise<Project> => {
  const response = await apiService.get<Project>(`/admin/projects/${id}`);
  return response.data;
};

export const adminCreateProject = async (
  projectData: ProjectInput
): Promise<Project> => {
  const response = await apiService.post<Project>(
    "/admin/projects",
    projectData
  );
  return response.data;
};

export const adminUpdateProject = async (
  id: string,
  projectData: Partial<ProjectInput>
): Promise<Project> => {
  const response = await apiService.put<Project>(
    `/admin/projects/${id}`,
    projectData
  );
  return response.data;
};

export const adminDeleteProject = async (id: string): Promise<void> => {
  await apiService.delete(`/admin/projects/${id}`);
};

export { apiService };

// Interface for paginated content page search results
export interface ContentPageSearchResultItem {
  _id: string;
  slug: string;
  title: string;
  metaDescription?: string;
  createdAt: string;
  // score?: number; // If you want to show relevance score from $meta: textScore
}

export interface ContentSearchApiResponse {
  results: ContentPageSearchResultItem[];
  page: number;
  totalPages: number;
  totalResults: number;
}

// Search content pages (Public)
export const searchContentPagesPublic = async (
  query: string,
  page: number = 1,
  limit: number = 10
): Promise<ContentSearchApiResponse> => {
  const params = new URLSearchParams();
  params.append("q", query);
  params.append("page", page.toString());
  params.append("limit", limit.toString());

  const response = await apiService.get<ContentSearchApiResponse>(
    "/content/pages/search", // Correct public endpoint
    { params }
  );
  return response.data;
};

// Enrollment API functions
export const submitEnrollment = async (
  enrollmentData: EnrollmentInput
): Promise<EnrollmentResponse> => {
  const response = await apiService.post("/enrollments/submit", enrollmentData);
  return response.data;
};

export const getEnrollments = async (
  page: number = 1,
  limit: number = 10,
  status?: string
): Promise<EnrollmentsApiResponse> => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });

  if (status) {
    params.append("status", status);
  }

  const response = await apiService.get(`/enrollments?${params.toString()}`);
  return response.data;
};

export const updateEnrollmentStatus = async (
  id: string,
  status: "pending" | "contacted" | "enrolled" | "declined",
  notes?: string
): Promise<{
  success: boolean;
  message: string;
  data: Enrollment;
}> => {
  const response = await apiService.put(`/enrollments/${id}/status`, {
    status,
    notes,
  });
  return response.data;
};

export interface Enrollment {
  _id: string;
  name: string;
  email: string;
  submissionDate: string;
  status: "pending" | "contacted" | "enrolled" | "declined";
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface EnrollmentInput {
  name: string;
  email: string;
}

export interface EnrollmentResponse {
  success: boolean;
  message: string;
  data: {
    id: string;
    name: string;
    email: string;
    submissionDate: string;
  };
}

export interface EnrollmentsApiResponse {
  enrollments: Enrollment[];
  page: number;
  totalPages: number;
  total: number;
}
